
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { User, Smartphone, Monitor, Wifi, ExternalLink, Shield, Activity, UserPlus, UserMinus, RefreshCw, ShieldBan, Trash2, Copy, Check, Hash } from "lucide-react";
import { useQuery as useConvexQuery, useAction as useConvexAction, useMutation as useConvexMutation } from "convex/react";
import { api } from "../../api";
import { type GenericId as Id } from "convex/values";
import { toConvexId } from "@/types/convex";
import { getDeviceStatus, getDeviceStatusInfo, getDeviceStatusBadgeColor, type DeviceStatus } from "@/utils/deviceStatus";
import { DeviceLevelSelector } from "./DeviceLevelSelector";
import { LinkUserModal } from "./LinkUserModal";
import { useDeviceUserLinking } from "@/hooks/useDeviceUserLinking";
import { useState, useMemo } from "react";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { UserSideView } from "./enhanced/UserSideView";
import { EnhancedUser } from "./enhanced/EnhancedUsersSection";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { buildConvexDashboardUrl } from "@/utils/convexDashboardUrl";
// Jamf base URL: https://jamf.kphone.org
import { jamfBaseUrl } from "@/config/environment";

interface Device {
  id: string;
  deviceConfig?: string;
  name: string;
  user: string;
  os: string;
  version: string;
  status: DeviceStatus;
  lastSync: string;
  group: string;
  serial?: string;
  jssId?: string | number;
  level?: {
    id: string;
    name: string;
  };
  user_info?: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    device_management_type: "self_managed" | "chaver_managed" | "org_managed";
    parent?: {
      id: string;
      first_name: string;
      last_name: string;
      email: string;
    };
    organization?: {
      id: string;
      name: string;
    };
  };
}

interface DeviceInventoryTabProps {
  device: Device;
  onNavigateToUser?: (userId: string) => void;
}

export const DeviceInventoryTab = ({ device, onNavigateToUser }: DeviceInventoryTabProps) => {
  const [isLinkUserModalOpen, setIsLinkUserModalOpen] = useState(false);
  const [isChangeDeviceModalOpen, setIsChangeDeviceModalOpen] = useState(false);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false);
  const [newSerialNumber, setNewSerialNumber] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);
  const [isUpdatingAds, setIsUpdatingAds] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [copiedDeviceCode, setCopiedDeviceCode] = useState(false);
  const [isUserSideViewOpen, setIsUserSideViewOpen] = useState(false);
  const { isLinking, linkUserToDevice, unlinkUserFromDevice } = useDeviceUserLinking();
  const { toast } = useToast();
  const { isSuperAdmin } = useIsAdmin();
  const navigate = useNavigate();

  // Note: Jamf update action not available in current API
  // const updateDeviceAction = useConvexAction(api.devices.jamf.update);
  
  // Mutation for updating ads settings
  const updateAdsMutation = useConvexMutation(api.devices.ads.update);
  
  // Action for deleting device
  const deleteDeviceAction = useConvexAction(api.devices.delete.deleteDevice);

  // Use devices.get.details to get comprehensive device information
  const deviceDetails = useConvexQuery(api.devices.get.details, {
    device: device.id as Id<"devices">
  });

  const handleUserClick = (userId: string) => {
    if (onNavigateToUser) {
      onNavigateToUser(userId);
    } else {
      // Navigate to users section with the specific user
      const currentUrl = new URL(window.location.href);
      currentUrl.pathname = '/section/users';
      currentUrl.searchParams.set('userId', userId);
      window.location.href = currentUrl.toString();
    }
  };

  const handleLinkUser = () => {
    setIsLinkUserModalOpen(true);
  };

  const handleSelectUser = async (userId: string) => {
    const success = await linkUserToDevice(device.id, userId);
    if (success) {
      setIsLinkUserModalOpen(false);
      // TODO: Refresh device data to show the newly linked user
    }
  };

  const handleUnlinkUser = async () => {
    const success = await unlinkUserFromDevice(device.id);
    if (success) {
      // TODO: Refresh device data to reflect the unlinked user
    }
  };

  const handleChangeDevice = () => {
    if (!newSerialNumber.trim()) {
      toast({
        title: "Error",
        description: "Please enter a serial number",
        variant: "destructive",
      });
      return;
    }
    setIsChangeDeviceModalOpen(false);
    setIsConfirmationOpen(true);
  };

  const handleConfirmChange = async () => {
    setIsUpdating(true);
    try {
      // Note: Jamf update action not available in current API
      // await updateDeviceAction({
      //   device: device.id as Id<"devices">,
      //   serial: newSerialNumber.trim()
      // });

      toast({
        title: "Not Available",
        description: "Device update via Jamf is not currently available in the API",
        variant: "destructive",
      });

      setIsConfirmationOpen(false);
      setNewSerialNumber("");
    } catch (error) {
      console.error("Error updating device:", error);
      toast({
        title: "Error",
        description: "Failed to update device. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCancelChange = () => {
    setIsChangeDeviceModalOpen(false);
    setIsConfirmationOpen(false);
    setNewSerialNumber("");
  };

  const handleAdsToggle = async (checked: boolean) => {
    setIsUpdatingAds(true);
    try {
      await updateAdsMutation({
        device: device.id as Id<"devices">,
        block: checked
      });

      toast({
        title: "Success",
        description: `Ads blocking ${checked ? 'enabled' : 'disabled'} for this device`,
      });
    } catch (error) {
      console.error("Error updating ads settings:", error);
      toast({
        title: "Error",
        description: "Failed to update ads settings. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUpdatingAds(false);
    }
  };

  const handleDeleteDevice = async () => {
    setIsDeleting(true);
    try {
      await deleteDeviceAction({
        device: device.id as Id<"devices">
      });

      toast({
        title: "Success",
        description: "Device deleted successfully",
      });

      setIsDeleteConfirmationOpen(false);
      
      // Navigate back to devices list
      navigate('/section/devices');
    } catch (error) {
      console.error("Error deleting device:", error);
      toast({
        title: "Error",
        description: "Failed to delete device. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCopyDeviceCode = async () => {
    const deviceCode = deviceDetails?.device?.code;
    if (!deviceCode) {
      toast({
        title: "Error",
        description: "No device code available to copy",
        variant: "destructive",
      });
      return;
    }

    try {
      await navigator.clipboard.writeText(deviceCode);
      setCopiedDeviceCode(true);
      toast({
        title: "Copied!",
        description: "Device code copied to clipboard",
      });
      setTimeout(() => setCopiedDeviceCode(false), 2000);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to copy device code",
        variant: "destructive",
      });
    }
  };


  // Use the actual user data from the device details
  const deviceUser = deviceDetails?.user;
  const displayLevel = deviceDetails?.level || device.level;

  // Store email in a stable variable to prevent hook condition changes
  const userEmail = deviceUser?.email || null;

  // Fetch all users to find the matching user by email
  // Always call the hook but skip if no deviceDetails yet (prevents hook order violations)
  const allUsersData = useConvexQuery(
    api.users.list.all,
    deviceDetails && userEmail ? {
      paginationOpts: {
        cursor: null,
        numItems: 100
      }
    } : "skip"
  );

  // Find the user that matches the device's user email (no memoization to avoid array reference issues)
  const matchingUser = userEmail && allUsersData?.page
    ? allUsersData.page.find((user: any) => 
        user.email?.toLowerCase() === userEmail.toLowerCase()
      )
    : null;
  
  console.log('Device user email:', userEmail);
  console.log('Matching user found:', matchingUser);

  // Transform to EnhancedUser format for the side view
  // Create a fallback user object even if not found in database
  const enhancedUser: EnhancedUser | null = useMemo(() => {
    if (!deviceUser) return null;
    
    // If we found the user in the database, use their ID and data
    if (matchingUser) {
      return {
        id: matchingUser._id,
        email: deviceUser.email || '',
        first_name: deviceUser.firstName || '',
        last_name: deviceUser.lastName || '',
        status: 'active',
        device_management_type: matchingUser.identity || 'self_managed',
        is_device_owner: true,
        device_count: 1,
        managed_users_count: 0,
        management_roles: [],
        phone: deviceUser.phone,
        created_at: new Date().toISOString(),
      };
    }
    
    // Fallback: Create a temporary user object from device data
    // Use device ID as a temporary ID since we don't have the real user ID
    return {
      id: device.id, // Using device ID as fallback
      email: deviceUser.email || '',
      first_name: deviceUser.firstName || '',
      last_name: deviceUser.lastName || '',
      status: 'active',
      device_management_type: 'self_managed',
      is_device_owner: true,
      device_count: 1,
      managed_users_count: 0,
      management_roles: [],
      phone: deviceUser.phone,
      created_at: new Date().toISOString(),
    };
  }, [matchingUser?._id, matchingUser?.identity, deviceUser?.email, deviceUser?.firstName, deviceUser?.lastName, deviceUser?.phone, device.id]);

  // Calculate device status using actual values only
  const serialNumber = deviceDetails?.device?.serial;
  const jssId = deviceDetails?.device?.jssid;
  const profileId = deviceDetails?.device?.profile;
  const deviceStatus = getDeviceStatus(serialNumber, jssId);
  // Check if active device has a profile
  const hasProfile = deviceStatus === 'active' ? !!profileId : undefined;
  const statusInfo = getDeviceStatusInfo(deviceStatus, hasProfile);

  return (
    <div className="p-3 sm:p-6 space-y-4">
      {/* Device Status Section */}
      <div className="bg-white border border-gray-200 rounded-lg">
        <div className="flex items-center px-4 py-2 border-b border-gray-100 bg-gray-50">
          <Activity className="w-4 h-4 mr-2 text-gray-600" />
          <h3 className="text-sm font-medium text-gray-900">Device Status</h3>
          <Badge className={getDeviceStatusBadgeColor(deviceStatus, hasProfile) + " ml-auto"}>
            {statusInfo.label}
          </Badge>
        </div>
        <div className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className={`w-3 h-3 rounded-full ${deviceStatus === 'active' ? (hasProfile === false ? 'bg-teal-500' : 'bg-green-500') : deviceStatus === 'in_process' ? 'bg-yellow-500' : 'bg-blue-500'}`}></div>
              <div>
                <p className="text-sm font-medium text-gray-900">{statusInfo.label}</p>
                <p className="text-xs text-gray-500">{statusInfo.description}</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsChangeDeviceModalOpen(true)}
                disabled={isUpdating}
                className="h-8 px-3 text-xs"
              >
                <RefreshCw className="w-3 h-3 mr-1" />
                {deviceStatus === 'registered' ? 'Set Serial Number' : 'Change Device'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.open(buildConvexDashboardUrl(device.id), '_blank', 'noopener,noreferrer')}
                className="h-8 px-3 text-xs"
              >
                <ExternalLink className="w-3 h-3 mr-1" />
                View in Convex
              </Button>
            </div>
          </div>
          
          {/* Serial Number, JSS ID, and Profile ID in Device Status - conditional visibility */}
          {deviceStatus !== 'registered' && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4 pt-4 border-t border-gray-100">
              {/* Serial Number - show for prepared and active */}
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Serial Number</label>
                <p className="text-sm text-gray-900 mt-1">{serialNumber || "?"}</p>
              </div>
              
              {/* JSS ID - show only for active devices */}
              {deviceStatus === 'active' && (
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">JSS ID</label>
                  {jssId ? (
                    <a 
                      href={`${jamfBaseUrl}/mobileDevices.html?id=${jssId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:text-blue-800 underline mt-1 block"
                    >
                      {jssId}
                    </a>
                  ) : (
                    <p className="text-sm text-gray-900 mt-1">?</p>
                  )}
                </div>
              )}
              
              {/* Profile ID - show for prepared and active */}
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Profile ID</label>
                {deviceDetails?.device?.profile ? (
                  <a 
                    href={`${jamfBaseUrl}/iOSConfigurationProfiles.html?id=${deviceDetails.device.profile}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:text-blue-800 underline mt-1 block"
                  >
                    {deviceDetails.device.profile}
                  </a>
                ) : (
                  <p className="text-sm text-gray-900 mt-1">?</p>
                )}
              </div>
            </div>
          )}

          {/* Device Code */}
          <div className="pt-4 border-t border-gray-100">
            <div className="flex items-center justify-between gap-3">
              <div className="flex-1">
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Device Code</label>
                <div className="flex items-center gap-2 mt-1">
                  <code className="text-sm font-mono text-gray-900">
                    {deviceDetails?.device?.code || "No device code available"}
                  </code>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleCopyDeviceCode}
                    disabled={!deviceDetails?.device?.code}
                    className="h-10 px-3 flex-shrink-0"
                  >
                    {copiedDeviceCode ? (
                      <Check className="w-4 h-4 text-green-600" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </Button>
                </div>
                {!deviceDetails?.device?.code && (
                  <p className="text-xs text-gray-500 mt-1">Six-digit device code will be available once the device is set up</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* User Information Section */}
      <div className="bg-white border border-gray-200 rounded-lg">
        <div className="flex items-center px-4 py-2 border-b border-gray-100 bg-gray-50">
          <User className="w-4 h-4 mr-2 text-gray-600" />
          <h3 className="text-sm font-medium text-gray-900">User Information</h3>
        </div>
        <div className="p-4 space-y-3">
          {deviceUser ? (
            <>
              {/* Primary User Info */}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div>
                    <p className="font-medium text-gray-900">
                      {deviceUser.firstName && deviceUser.lastName 
                        ? `${deviceUser.firstName} ${deviceUser.lastName}`
                        : 'Unknown User'
                      }
                    </p>
                    <p className="text-sm text-gray-500">{deviceUser.email || 'No email available'}</p>
                    {deviceUser.phone && (
                      <p className="text-xs text-gray-500">{deviceUser.phone}</p>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => matchingUser?._id && handleUserClick(matchingUser._id)}
                    disabled={!matchingUser}
                    className="h-8 px-3 text-xs"
                  >
                    {allUsersData === undefined ? 'Loading...' : 'View Profile'}
                    <ExternalLink className="w-3 h-3 ml-1" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleUnlinkUser}
                    disabled={isLinking}
                    className="h-8 px-3 text-xs text-red-600 border-red-200 hover:bg-red-50"
                  >
                    <UserMinus className="w-3 h-3 mr-1" />
                    Unlink
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                  <User className="w-5 h-5 text-gray-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">No user linked to this device</p>
                  <p className="text-xs text-gray-500">Link a user to manage device ownership and permissions</p>
                </div>
              </div>
              <Button
                onClick={handleLinkUser}
                size="sm"
                disabled={isLinking}
                className="h-8 px-3 text-xs flex items-center gap-1"
              >
                <UserPlus className="w-3 h-3" />
                Link User
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Level Section */}
      <div className="bg-white border border-gray-200 rounded-lg">
        <div className="flex items-center px-4 py-2 border-b border-gray-100 bg-gray-50">
          <Shield className="w-4 h-4 mr-2 text-gray-600" />
          <h3 className="text-sm font-medium text-gray-900">Level Assignment</h3>
          {displayLevel && (
            <Badge variant="secondary" className="ml-auto flex items-center gap-1">
              <Shield className="w-3 h-3" />
              {typeof displayLevel === 'string' ? displayLevel : (displayLevel.name || String(displayLevel))}
            </Badge>
          )}
        </div>
        <div className="p-4">
          <DeviceLevelSelector 
            deviceId={device.id} 
            deviceConfig={deviceDetails?.device?.deviceConfig}
            deviceName={device.name}
          />
        </div>
      </div>

      {/* Ads Section */}
      <div className="bg-white border border-gray-200 rounded-lg">
        <div className="flex items-center px-4 py-2 border-b border-gray-100 bg-gray-50">
          <ShieldBan className="w-4 h-4 mr-2 text-gray-600" />
          <h3 className="text-sm font-medium text-gray-900">Ads</h3>
        </div>
        <div className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div>
                <p className="text-sm font-medium text-gray-900">Block All Ads</p>
                <p className="text-xs text-gray-500">Prevent advertisements from displaying on this device</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                checked={deviceDetails?.adBlock || false}
                onCheckedChange={handleAdsToggle}
                disabled={isUpdatingAds}
              />
              {isUpdatingAds && (
                <RefreshCw className="w-4 h-4 animate-spin text-gray-400" />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Device Information Section - only show for active devices */}
      {deviceStatus === 'active' && (
        <div className="bg-white border border-gray-200 rounded-lg">
          <div className="flex items-center px-4 py-2 border-b border-gray-100 bg-gray-50">
            <Smartphone className="w-4 h-4 mr-2 text-gray-600" />
            <h3 className="text-sm font-medium text-gray-900">Device Information</h3>
          </div>
          <div className="p-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Device Name</label>
                <p className="text-sm text-gray-900 mt-1">{device.name || "?"}</p>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">OS Version</label>
                <p className="text-sm text-gray-900 mt-1">{device.os || "?"} {device.version || ""}</p>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Model</label>
                <p className="text-sm text-gray-900 mt-1">?</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Device Config</label>
                <p className="text-sm text-gray-900 mt-1">{deviceDetails?.device?.deviceConfig || "?"}</p>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Storage</label>
                <p className="text-sm text-gray-900 mt-1">?</p>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Battery Level</label>
                <p className="text-sm text-gray-900 mt-1">?</p>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Memory</label>
                <p className="text-sm text-gray-900 mt-1">?</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Network Information Section - only show for active devices */}
      {deviceStatus === 'active' && (
        <div className="bg-white border border-gray-200 rounded-lg">
          <div className="flex items-center px-4 py-2 border-b border-gray-100 bg-gray-50">
            <Wifi className="w-4 h-4 mr-2 text-gray-600" />
            <h3 className="text-sm font-medium text-gray-900">Network Information</h3>
          </div>
          <div className="p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">WiFi Status</label>
                <p className="text-sm text-gray-900 mt-1">?</p>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Network Name</label>
                <p className="text-sm text-gray-900 mt-1">?</p>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">IP Address</label>
                <p className="text-sm text-gray-900 mt-1">?</p>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Cellular</label>
                <p className="text-sm text-gray-900 mt-1">?</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Device Section - only show for super admins */}
      {isSuperAdmin && (
        <div className="bg-white border border-red-200 rounded-lg">
          <div className="flex items-center px-4 py-2 border-b border-red-100 bg-red-50">
            <Trash2 className="w-4 h-4 mr-2 text-red-600" />
            <h3 className="text-sm font-medium text-red-900">Danger Zone</h3>
          </div>
          <div className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div>
                  <p className="text-sm font-medium text-red-900">Delete Device</p>
                  <p className="text-xs text-red-600">Permanently remove this device from the system. This action cannot be undone.</p>
                </div>
              </div>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setIsDeleteConfirmationOpen(true)}
                disabled={isDeleting}
                className="h-8 px-3 text-xs"
              >
                <Trash2 className="w-3 h-3 mr-1" />
                {isDeleting ? "Deleting..." : "Delete Device"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Link User Modal */}
      <LinkUserModal
        isOpen={isLinkUserModalOpen}
        onClose={() => setIsLinkUserModalOpen(false)}
        onSelectUser={handleSelectUser}
        deviceName={device.name}
        isLinking={isLinking}
      />

      {/* Change Device Modal */}
      <Dialog open={isChangeDeviceModalOpen} onOpenChange={setIsChangeDeviceModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Change Device</DialogTitle>
            <DialogDescription>
              Enter the serial number of the device you want to switch to. This will update the device information.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="serial-number">Serial Number</Label>
              <Input
                id="serial-number"
                placeholder="Enter serial number"
                value={newSerialNumber}
                onChange={(e) => setNewSerialNumber(e.target.value)}
                className="w-full"
              />
            </div>
          </div>
          <DialogFooter className="sm:justify-start">
            <Button
              type="button"
              variant="secondary"
              onClick={handleCancelChange}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleChangeDevice}
            >
              Change Device
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog */}
      <AlertDialog open={isConfirmationOpen} onOpenChange={setIsConfirmationOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Device Change</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to change this device to serial number "{newSerialNumber}"? 
              This will update all device information and may affect the device's configuration and policies.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelChange} disabled={isUpdating}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmChange} disabled={isUpdating}>
              {isUpdating ? "Updating..." : "Yes, Change Device"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteConfirmationOpen} onOpenChange={setIsDeleteConfirmationOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Device</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to permanently delete "{device.name}"? 
              This will remove all device information, user associations, and configurations.
              This action cannot be undone and may affect users who depend on this device.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setIsDeleteConfirmationOpen(false)} disabled={isDeleting}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteDevice} disabled={isDeleting} className="bg-red-600 hover:bg-red-700">
              {isDeleting ? "Deleting..." : "Yes, Delete Device"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* User Profile Side View */}
      <UserSideView 
        user={enhancedUser}
        isOpen={isUserSideViewOpen}
        onClose={() => setIsUserSideViewOpen(false)}
      />
    </div>
  );
};
